import { createQueryKeys } from "@lukemorales/query-key-factory";

import type { CoreApiClient } from "../client";
import type {
  CurrentHslope,
  GetHeatingCurveHistoryResponseBody,
  GetHslopeHistoryResponseBody,
  HeatingCurve,
  HeatingCurveHistoryFilters,
  HeatingNetwork,
  HslopeHistoryFilters,
} from "../types/heating-network.type";
import type { Planning } from "../types/planning.type";

export const HEATING_CURVE_HISTORY_PAGE_LIMIT = 20;
export const HSLOPE_HISTORY_PAGE_LIMIT = 10;

export const createHeatingNetworkQueries = (api: CoreApiClient) => {
  const { client } = api;

  const list = async (buildingId: string): Promise<HeatingNetwork[]> => {
    const { data, error } = await client.GET(`/buildings/{buildingId}/heating-networks`, {
      params: { path: { buildingId } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  const plannings = async (heatingNetworkId: string): Promise<Planning[]> => {
    const { data, error } = await client.GET(`/heating-networks/{heatingNetworkId}/plannings`, {
      params: { path: { heatingNetworkId } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  const curves = async (heatingNetworkId: string): Promise<HeatingCurve[]> => {
    const { data, error } = await client.GET(`/heating-networks/{heatingNetworkId}/heating-curves`, {
      params: { path: { heatingNetworkId } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  const curveHistory = async (
    heatingNetworkId: string,
    filters: HeatingCurveHistoryFilters,
    cursor?: string,
  ): Promise<GetHeatingCurveHistoryResponseBody> => {
    const { data, error } = await client.GET(`/heating-networks/{heatingNetworkId}/heating-curve-history`, {
      params: { path: { heatingNetworkId }, query: { ...filters, cursor } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  const currentHslopes = async (heatingNetworkId: string, externalTemperature: number): Promise<CurrentHslope[]> => {
    const { data, error } = await client.GET(`/heating-networks/{heatingNetworkId}/current-hslopes`, {
      params: { path: { heatingNetworkId }, query: { externalTemperature } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  const hslopeHistory = async (
    heatingNetworkId: string,
    historyId: string,
    filters: HslopeHistoryFilters,
    cursor?: string,
  ): Promise<GetHslopeHistoryResponseBody> => {
    const { data, error } = await client.GET(
      `/heating-networks/{heatingNetworkId}/heating-curve-history/{historyId}/hslopes`,
      {
        params: { path: { heatingNetworkId, historyId }, query: { ...filters, cursor } },
      },
    );
    if (error) throw new Error(error.message);
    return data;
  };

  return {
    list,
    plannings,
    curves,
    curveHistory,
    currentHslopes,
    hslopeHistory,
    queries: createQueryKeys("heatingNetwork", {
      building: (buildingId: string) => ({
        queryKey: [buildingId],
        queryFn: () => list(buildingId),
      }),
      plannings: (heatingNetworkId: string) => ({
        queryKey: [heatingNetworkId, "plannings"],
        queryFn: () => plannings(heatingNetworkId),
      }),
      heatingCurves: (heatingNetworkId: string) => ({
        queryKey: [heatingNetworkId, "heating-curves"],
        queryFn: () => curves(heatingNetworkId),
      }),
      heatingCurveHistory: (heatingNetworkId: string, filters: HeatingCurveHistoryFilters) => ({
        queryKey: [heatingNetworkId, "heating-curve-history", filters],
        queryFn: ({ pageParam }: { pageParam?: string }) => curveHistory(heatingNetworkId, filters, pageParam),
      }),
      currentHslopes: (heatingNetworkId: string, externalTemperature: number) => ({
        queryKey: [heatingNetworkId, "current-hslopes", externalTemperature],
        queryFn: () => currentHslopes(heatingNetworkId, externalTemperature),
      }),
      hslopeHistory: (heatingNetworkId: string, historyId: string, filters: HslopeHistoryFilters) => ({
        queryKey: [heatingNetworkId, historyId, "hslopes", filters],
        queryFn: ({ pageParam }: { pageParam?: string }) =>
          hslopeHistory(heatingNetworkId, historyId, filters, pageParam),
      }),
    }),
  };
};
